import {
    MeshTransmissionMaterial,
    useFBO,
} from '@react-three/drei';
import {
    useFrame,
    useThree,
} from '@react-three/fiber';
import {
    memo,
    useMemo,
    useRef,
} from 'react';
import type { RefObject } from 'react';
import {
    FrontSide,
    NoToneMapping,
} from 'three';
import type {
    Mesh,
    MeshPhysicalMaterial,
    Texture,
} from 'three';
import type { ModelViewerPedestalLayout } from '../Pedestal';
import { STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY } from './config';

interface Props {
    layout: ModelViewerPedestalLayout
    parentRef: RefObject<Mesh>
}

type TransmissionMaterial = MeshPhysicalMaterial & {
    buffer: Texture
};

const TRANSMISSION_LONG_EDGE = 1024;

function getAspectBoundedSize(
    width:number,
    height:number,
    longestEdge:number,
):[number, number] {
    const safeWidth = Math.max(width, 1);
    const safeHeight = Math.max(height, 1);

    return safeWidth >= safeHeight
        ? [
            longestEdge,
            Math.max(1, Math.round(longestEdge * safeHeight / safeWidth)),
        ]
        : [
            Math.max(1, Math.round(longestEdge * safeWidth / safeHeight)),
            longestEdge,
        ];
}

/**
 * Captures an aspect-correct, fixed-size transmission buffer independent of
 * Canvas DPR. A single scene capture feeds the front faces; double screen-space
 * refraction through a smaller backside buffer produced clamp streaks and
 * amplified noise around grazing bevels.
 *
 * Drei's material supplies the optical shader, but its normal `resolution`
 * prop leaves one target dimension tied to Canvas DPR. Driving its buffer here
 * keeps 4x render-server previews within a predictable memory budget.
 */
export default memo(function PedestalGlassMaterial({
    layout,
    parentRef,
}:Props) {
    const size = useThree(state => state.size);
    const materialRef = useRef<TransmissionMaterial>(null);
    const [transmissionWidth, transmissionHeight] = useMemo(() => (
        getAspectBoundedSize(
            size.width,
            size.height,
            TRANSMISSION_LONG_EDGE,
        )
    ), [size.height, size.width]);
    const transmissionTarget = useFBO(
        transmissionWidth,
        transmissionHeight,
        {
            depthBuffer: true,
            samples: 0,
        },
    );
    const smallestDimension = Math.max(
        Math.min(layout.width, layout.depth, layout.height),
        0.001,
    );
    const opticalThickness = smallestDimension * 0.5;
    // The shared Studio environment contains large HDR softboxes intended to
    // shape product materials. Polished glass mirrors those rectangles almost
    // literally, so keep their contribution as an edge highlight and let the
    // real scene transmission provide most of the pedestal's visual detail.
    const envMapIntensity = 0.45;

    useFrame(({camera, gl, scene}) => {
        const material = materialRef.current;
        const parent = parentRef.current;
        if(
            !material
            || !parent
            || !parent.visible
            || gl.getRenderTarget() !== null
        ) return;

        const previousRenderTarget = gl.getRenderTarget();
        const previousToneMapping = gl.toneMapping;
        const previousVisibility = parent.visible;
        const sceneUserData = scene.userData as Record<string, unknown>;
        const previousTransmissionCaptureState = (
            sceneUserData[
                STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
            ]
        );

        try {
            // Match Drei's transmission capture: store linear scene color so
            // the final material is tone-mapped exactly once.
            gl.toneMapping = NoToneMapping;

            parent.visible = false;
            sceneUserData[
                STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
            ] = true;
            gl.setRenderTarget(transmissionTarget);
            gl.clear(true, true, true);
            gl.render(scene, camera);
        } finally {
            sceneUserData[
                STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
            ] = previousTransmissionCaptureState;
            parent.visible = previousVisibility;
            material.buffer = transmissionTarget.texture;
            material.envMapIntensity = envMapIntensity;
            material.side = FrontSide;
            material.thickness = opticalThickness;
            gl.setRenderTarget(previousRenderTarget);
            gl.toneMapping = previousToneMapping;
        }
    });

    return <MeshTransmissionMaterial
        ref={materialRef}
        anisotropicBlur={0.05}
        attenuationColor="#eaf3ff"
        attenuationDistance={smallestDimension * 4}
        // Supplying our own buffer disables Drei's automatic capture. Tiny
        // placeholder targets keep its internal hooks effectively free.
        backsideResolution={1}
        buffer={transmissionTarget.texture}
        chromaticAberration={0.01}
        clearcoat={1}
        clearcoatRoughness={0.06}
        color="#ffffff"
        dithering
        distortion={0.012}
        distortionScale={1.1}
        envMapIntensity={envMapIntensity}
        ior={1.47}
        metalness={0}
        resolution={1}
        roughness={0.09}
        samples={4}
        temporalDistortion={0}
        thickness={opticalThickness}
        transmission={1}
    />;
});
